import { type StateCreator } from 'zustand';

export type ToolCallStatusEntry = {
  toolCallId: string;
  toolCallName: string;
  status: 'working' | 'completed' | 'error';
};

export type ToolCallStatusSlice = {
  /** Tool call statuses keyed by thread ID. */
  byThread: Record<string, ToolCallStatusEntry[]>;
  /** Adds a tool call entry with 'working' status. Idempotent — skips if entry already exists. */
  startToolCall: (threadId: string, toolCallId: string, toolCallName: string) => void;
  /** Updates an existing tool call entry to 'completed' status. */
  completeToolCall: (threadId: string, toolCallId: string) => void;
  /** Marks all 'working' tool calls for a thread as 'error'. */
  failToolCalls: (threadId: string) => void;
  /**
   * Removes all of a thread's entries. Entries without a backing tool message render as
   * synthetic chips (see useMessages), so a message rollback must take the run's chips
   * with it or they linger as ghosts.
   */
  clearToolCalls: (threadId: string) => void;
};

export type CreateToolCallStatusSlice = () => StateCreator<ToolCallStatusSlice, [], [], ToolCallStatusSlice>;

export const createToolCallStatusSlice: CreateToolCallStatusSlice = () => (set, get) => ({
  byThread: {},
  clearToolCalls: (threadId) => {
    if (!get().byThread[threadId]) {
      return;
    }

    const byThread = { ...get().byThread };
    delete byThread[threadId];
    set({ byThread });
  },
  completeToolCall: (threadId, toolCallId) => {
    const existing = get().byThread[threadId];

    if (!existing) {
      return;
    }

    set({
      byThread: {
        ...get().byThread,
        [threadId]: existing.map((entry) =>
          entry.toolCallId === toolCallId ? { ...entry, status: 'completed' as const } : entry,
        ),
      },
    });
  },
  failToolCalls: (threadId) => {
    const existing = get().byThread[threadId];

    if (!existing) {
      return;
    }

    const hasWorking = existing.some((entry) => entry.status === 'working');

    if (!hasWorking) {
      return;
    }

    set({
      byThread: {
        ...get().byThread,
        [threadId]: existing.map((entry) =>
          entry.status === 'working' ? { ...entry, status: 'error' as const } : entry,
        ),
      },
    });
  },
  startToolCall: (threadId, toolCallId, toolCallName) => {
    const existing = get().byThread[threadId] ?? [];

    if (existing.some((entry) => entry.toolCallId === toolCallId)) {
      return;
    }
    set({
      byThread: {
        ...get().byThread,
        [threadId]: [...existing, { status: 'working' as const, toolCallId, toolCallName }],
      },
    });
  },
});
